/* =============================================================================
   DAILE v2 (dv) — artwork product pages (sections/dv-pdp-*).
   Modules (window.DV registry, scoped to one .shopify-section root):

   dv-pdp          gallery (scroll-snap track, arrows, thumbs, counter, zoom
                   dialog, #set: filter), set picker (port of daile-mvp.js
                   initPdpTiers / startFromUrl / syncVariantInput /
                   initTierGallery), generic variant picker, quantity,
                   sticky buy pill, reminder (port of initRemind, Klaviyo
                   client API), "recently viewed" localStorage.
                   Photo mode (custom-puzzle, [data-photo-mode]): no cart
                   form; the CTA and the sticky pill link to the customizer
                   with ?set=<slug>, and "Add your photo" hands the file over
                   (port of daile-pdp.js initPdpUpload): IndexedDB 'daile-cz'
                   v1, store 'photos', key 'photo_pdp-pending' (File, or
                   {buf, type} where a File cannot be stored), then
                   <customizer>?pdp=1&set=<slug>; no IndexedDB → the
                   customizer without the photo.
   dv-pdp-faq      hides the NFC link when the page has no NFC dialog.
   dv-pdp-artist   promo code copy button.

   CART CONTRACT (assets/daile-cart.js handleAdd, locked file):
   on every set change the form gets
     form._daileMvpSelection = { variantId, available, addonVariantId }
   (addonVariantId = glue-kit variant when "Add Glue Kit" is ticked and the
   glue is available), input[name=id] carries the chosen variant (disabled
   when unavailable). daile-cart.js intercepts the submit in the capture
   phase on document; our own capture listener on the form only re-syncs and
   blocks the submit when the selection is unavailable — it never stops the
   event otherwise. Prices are ready strings from Liquid (daile-money).
   ============================================================================= */
(() => {
  'use strict';
  if (!window.DV || typeof window.DV.register !== 'function' || window.__dvPdpLoaded) return;
  window.__dvPdpLoaded = true;
  const DV = window.DV;

  const reducedMotion = () => !!(DV.reducedMotion && DV.reducedMotion.matches);
  const isRTL = () => (typeof DV.isRTL === 'function' ? DV.isRTL() : document.documentElement.dir === 'rtl');

  /* Strings from a JSON <script> are not HTML-decoded: a money format or a
     daile-t string with an entity (&euro;, &rsquo;) would show raw. A
     <textarea> decodes entities without running anything. */
  const decoder = document.createElement('textarea');
  const decode = (s) => {
    if (!s || String(s).indexOf('&') === -1) return s || '';
    decoder.innerHTML = String(s);
    return decoder.value;
  };

  /* ------------------------------------------------------------------ gallery */

  function initGallery(gallery, on) {
    const track = gallery.querySelector('[data-dv-pdp-track]');
    if (!track) return null;
    const slides = Array.from(track.querySelectorAll('[data-dv-pdp-slide]'));
    const thumbItems = Array.from(gallery.querySelectorAll('[data-dv-pdp-thumb-item]'));
    const thumbsList = gallery.querySelector('[data-dv-pdp-thumbs]');
    const prev = gallery.querySelector('[data-dv-pdp-prev]');
    const next = gallery.querySelector('[data-dv-pdp-next]');
    const countCurrent = gallery.querySelector('[data-dv-pdp-count-current]');
    const countTotal = gallery.querySelector('[data-dv-pdp-count-total]');
    const root = gallery.closest('.shopify-section') || document;
    const zoomDialog = root.querySelector('[data-dv-pdp-zoom-dialog]');
    const zoomImg = zoomDialog ? zoomDialog.querySelector('[data-dv-pdp-zoom-img]') : null;
    let current = 0;
    let zoomIndex = 0;
    let raf = 0;

    const visible = () => slides.filter((s) => !s.hidden);

    /* Horizontal delta between an element and its scroller's start edge. */
    const deltaTo = (el, scroller) => {
      const a = el.getBoundingClientRect();
      const b = scroller.getBoundingClientRect();
      return isRTL() ? a.right - b.right : a.left - b.left;
    };

    const indexNow = () => {
      const vis = visible();
      let best = 0;
      let bestD = Infinity;
      vis.forEach((s, i) => {
        const d = Math.abs(deltaTo(s, track));
        if (d < bestD) { bestD = d; best = i; }
      });
      return best;
    };

    const pauseOthers = (activeSlide) => {
      slides.forEach((s) => {
        if (s === activeSlide) return;
        s.querySelectorAll('video').forEach((v) => { if (!v.paused) v.pause(); });
      });
    };

    const update = () => {
      raf = 0;
      const vis = visible();
      current = Math.min(indexNow(), Math.max(vis.length - 1, 0));
      if (countCurrent) countCurrent.textContent = String(vis.length ? current + 1 : 0);
      if (countTotal) countTotal.textContent = String(vis.length);
      if (prev) prev.disabled = current <= 0;
      if (next) next.disabled = current >= vis.length - 1;
      const active = vis[current];
      thumbItems.forEach((item, i) => {
        const btn = item.querySelector('[data-dv-pdp-thumb]');
        const on = slides[i] === active;
        if (btn) btn.setAttribute('aria-current', on ? 'true' : 'false');
        if (on && thumbsList && !item.hidden) {
          /* Keep the active thumb in view: scroll the thumb row only, never the page. */
          const a = item.getBoundingClientRect();
          const b = thumbsList.getBoundingClientRect();
          if (a.left < b.left || a.right > b.right) {
            const centre = (a.left + a.right) / 2 - (b.left + b.right) / 2;
            thumbsList.scrollBy({ left: centre, behavior: reducedMotion() ? 'auto' : 'smooth' });
          }
        }
      });
      pauseOthers(active);
    };

    const schedule = () => { if (!raf) raf = requestAnimationFrame(update); };

    const goTo = (i, instant) => {
      const vis = visible();
      if (!vis.length) return;
      const target = vis[Math.max(0, Math.min(i, vis.length - 1))];
      const d = deltaTo(target, track);
      if (Math.abs(d) < 1) { schedule(); return; }
      track.scrollBy({ left: d, behavior: instant || reducedMotion() ? 'auto' : 'smooth' });
      schedule();
    };

    const goToSlide = (slide, instant) => {
      const i = visible().indexOf(slide);
      if (i >= 0) goTo(i, instant);
    };

    on(track, 'scroll', schedule, { passive: true });
    if (prev) on(prev, 'click', () => goTo(current - 1));
    if (next) on(next, 'click', () => goTo(current + 1));
    on(track, 'keydown', (e) => {
      if (e.target !== track) return;
      const fwd = isRTL() ? 'ArrowLeft' : 'ArrowRight';
      const back = isRTL() ? 'ArrowRight' : 'ArrowLeft';
      if (e.key === fwd) { e.preventDefault(); goTo(current + 1); }
      else if (e.key === back) { e.preventDefault(); goTo(current - 1); }
    });
    thumbItems.forEach((item, i) => {
      const btn = item.querySelector('[data-dv-pdp-thumb]');
      if (btn) on(btn, 'click', () => goToSlide(slides[i]));
    });
    on(window, 'resize', schedule, { passive: true });

    /* Zoom dialog (images only). */
    const zoomables = () => visible().filter((s) => s.querySelector('[data-dv-pdp-zoom]'));
    const showZoom = (i) => {
      const list = zoomables();
      if (!list.length || !zoomImg) return;
      zoomIndex = (i + list.length) % list.length;
      const btn = list[zoomIndex].querySelector('[data-dv-pdp-zoom]');
      const img = list[zoomIndex].querySelector('img');
      zoomImg.src = btn.getAttribute('data-dv-pdp-zoom');
      zoomImg.alt = img ? img.alt : '';
    };
    if (zoomDialog && zoomImg) {
      slides.forEach((slide) => {
        const btn = slide.querySelector('[data-dv-pdp-zoom]');
        if (!btn) return;
        on(btn, 'click', () => {
          showZoom(zoomables().indexOf(slide));
          DV.openDialog(zoomDialog, btn);
        });
      });
      const zPrev = zoomDialog.querySelector('[data-dv-pdp-zoom-prev]');
      const zNext = zoomDialog.querySelector('[data-dv-pdp-zoom-next]');
      if (zPrev) on(zPrev, 'click', () => showZoom(zoomIndex - 1));
      if (zNext) on(zNext, 'click', () => showZoom(zoomIndex + 1));
      on(zoomDialog, 'keydown', (e) => {
        const fwd = isRTL() ? 'ArrowLeft' : 'ArrowRight';
        const back = isRTL() ? 'ArrowRight' : 'ArrowLeft';
        if (e.key === fwd) { e.preventDefault(); showZoom(zoomIndex + 1); }
        else if (e.key === back) { e.preventDefault(); showZoom(zoomIndex - 1); }
      });
      on(zoomDialog, 'close', () => {
        const list = zoomables();
        if (list[zoomIndex]) goToSlide(list[zoomIndex], true);
      });
    }

    /* #set: filter. keys = ['mini', 'mini-300'] | ['classic'] | ['signature'].
       Untagged media always stay; 'none' never shows. `scroll` = move the
       track to the first photo of the set (never the page). */
    let lastKey = null;
    const applySet = (keys, scroll) => {
      if (!gallery.hasAttribute('data-dv-pdp-set-filter')) return;
      slides.forEach((slide, i) => {
        const raw = slide.getAttribute('data-dv-set');
        if (!raw) return;
        const list = raw.split(',').map((k) => k.trim());
        const show = !list.includes('none') && list.some((k) => keys.includes(k));
        slide.hidden = !show;
        if (thumbItems[i]) thumbItems[i].hidden = !show;
      });
      const key = keys.join('|');
      const changed = lastKey !== null && key !== lastKey;
      lastKey = key;
      requestAnimationFrame(() => {
        if (scroll && changed) {
          const first = visible().find((s) => s.hasAttribute('data-dv-set'));
          if (first) { goToSlide(first); return; }
        }
        if (current >= visible().length) goTo(0, true);
        schedule();
      });
    };

    const focusMedia = (mediaId) => {
      if (!mediaId) return;
      const slide = slides.find((s) => s.getAttribute('data-media-id') === String(mediaId));
      if (slide && !slide.hidden) goToSlide(slide);
    };

    schedule();
    return { applySet, focusMedia };
  }

  /* ------------------------------------------------------------ buy helpers */

  function buyState(pdp) {
    const photo = pdp.hasAttribute('data-photo-mode');
    const form = photo ? null : document.getElementById(pdp.getAttribute('data-form-id') || '');
    const scope = form || pdp;
    const idInput = form ? form.querySelector('[data-dv-pdp-id]') || form.querySelector('input[name="id"]') : null;
    const addBtn = scope.querySelector('[data-dv-pdp-add]');
    const addLabel = scope.querySelector('[data-dv-pdp-add-label]');
    const status = scope.querySelector('[data-dv-pdp-status]');
    const express = form ? form.querySelector('[data-dv-pdp-express]') : null;
    const sticky = pdp.querySelector('[data-dv-pdp-sticky]');
    const stickyBtn = sticky ? sticky.querySelector('[data-dv-pdp-sticky-add]') : null;
    const stickyLabel = sticky ? sticky.querySelector('[data-dv-pdp-sticky-add-label]') : null;
    const T = window.DAILE_CART_T || {};
    const t = {
      add: pdp.getAttribute('data-t-add') || T.js_pdp_add_to_cart || 'Add to cart',
      soldOut: pdp.getAttribute('data-t-sold-out') || 'Sold out',
      unavailable: T.js_cart_unavailable || pdp.getAttribute('data-t-unavailable') || 'This set is currently unavailable.',
      personalise: pdp.getAttribute('data-t-personalise') || 'Personalise this set'
    };

    /* Photo mode: the customizer address with the chosen set. */
    const czBase = pdp.getAttribute('data-customizer-url') || '/pages/customizer';
    const czUrl = (slug, pdpHandover) => {
      const q = [];
      if (pdpHandover) q.push('pdp=1');
      if (slug) q.push('set=' + encodeURIComponent(slug));
      return czBase + (q.length ? (czBase.indexOf('?') === -1 ? '?' : '&') + q.join('&') : '');
    };

    /* One place that writes the purchase state into the DOM. */
    const setPurchase = ({ variantId, available, hideExpress, setMode, slug }) => {
      if (photo) {
        [addBtn, stickyBtn].forEach((a) => {
          if (!a) return;
          if (available) {
            a.setAttribute('href', czUrl(slug, false));
            a.removeAttribute('aria-disabled');
            a.removeAttribute('role');
          } else {
            a.removeAttribute('href');
            a.setAttribute('aria-disabled', 'true');
            a.setAttribute('role', 'link');
          }
          a.classList.toggle('is-disabled', !available);
        });
        if (addLabel) addLabel.textContent = available ? t.personalise : t.unavailable;
        if (status) status.textContent = available ? '' : t.unavailable;
        return;
      }
      if (idInput) {
        const nextId = available && variantId ? String(variantId) : '';
        const changed = idInput.value !== nextId || idInput.disabled === !!available;
        idInput.value = nextId;
        idInput.disabled = !available;
        if (changed) {
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
          if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
            try { window.Shopify.PaymentButton.init(); } catch (e) { /* no dynamic checkout on this page */ }
          }
        }
      }
      const offLabel = setMode ? t.unavailable : t.soldOut;
      if (addBtn) {
        addBtn.disabled = !available;
        addBtn.setAttribute('aria-disabled', available ? 'false' : 'true');
      }
      if (addLabel) addLabel.textContent = available ? t.add : offLabel;
      if (status) status.textContent = available ? '' : offLabel;
      if (stickyBtn) stickyBtn.disabled = !available;
      if (stickyLabel) stickyLabel.textContent = available ? t.add : t.soldOut;
      if (form) {
        form.querySelectorAll('.shopify-payment-button').forEach((b) => {
          b.hidden = !available || !!hideExpress;
          b.style.display = !available || hideExpress ? 'none' : '';
        });
      }
      if (express) express.hidden = !available || !!hideExpress;
    };

    /* The sticky pill is moved to <body> (see initSticky): query it too. */
    const all = (selector) => {
      const list = Array.from(pdp.querySelectorAll(selector));
      if (sticky && !pdp.contains(sticky)) list.push(...sticky.querySelectorAll(selector));
      return list;
    };

    const setPrice = (money, compare) => {
      all('[data-dv-pdp-price-current]').forEach((el) => { el.textContent = money || ''; });
      all('[data-dv-pdp-price-compare]').forEach((el) => {
        el.textContent = compare || '';
        el.hidden = !compare;
      });
      all('[data-dv-pdp-sticky-price]').forEach((el) => { el.textContent = money || ''; });
    };

    return { photo, form, idInput, addBtn, sticky, stickyBtn, t, czUrl, all, setPurchase, setPrice };
  }

  const replaceVariantParam = (variantId, dropSet) => {
    try {
      const url = new URL(window.location.href);
      if (variantId) url.searchParams.set('variant', String(variantId));
      else url.searchParams.delete('variant');
      if (dropSet) url.searchParams.delete('set');
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) { /* old browser: keep the URL */ }
  };

  /* --------------------------------------------------------------- set picker */

  function initSets(pdp, buy, gallery, on) {
    const box = pdp.querySelector('[data-dv-pdp-sets]');
    if (!box) return null;
    const radios = Array.from(box.querySelectorAll('input[type="radio"][data-variant-id]'));
    if (!radios.length) return null;
    const glueWrap = box.querySelector('[data-dv-pdp-glue]');
    const glueInput = box.querySelector('[data-dv-pdp-glue-input]');
    const glueVariant = box.getAttribute('data-glue-variant') || null;
    const glueAvailable = box.getAttribute('data-glue-available') === 'true';
    const incs = Array.from(box.querySelectorAll('[data-dv-pdp-inc]'));
    const piecesWord = pdp.getAttribute('data-t-pieces') || 'pieces';
    const glueWith = pdp.getAttribute('data-t-glue-with') || '';
    const glueWithout = pdp.getAttribute('data-t-glue-without') || '';
    const state = { tier: 'classic', pieces: 1000, slug: 'classic', glue: false, available: false, variantId: null, priceText: '' };

    const selected = () => radios.find((r) => r.checked) || radios[0];

    const keysFor = (r) => {
      const tier = r.getAttribute('data-tier');
      const keys = [tier];
      if (tier === 'mini') keys.push('mini-' + r.getAttribute('data-pieces'));
      return keys;
    };

    const sync = () => {
      const r = selected();
      const tier = r.getAttribute('data-tier');
      const isMini = tier === 'mini';
      const withGlue = isMini && !!glueInput && glueInput.checked && !!glueVariant;
      const variantId = r.getAttribute('data-variant-id') || null;
      const variantAvailable = !!variantId && r.getAttribute('data-available') === 'true';
      const available = variantAvailable && (!withGlue || glueAvailable);

      state.tier = tier;
      state.slug = r.value;
      state.pieces = parseInt(r.getAttribute('data-pieces'), 10) || 0;
      state.glue = withGlue;
      state.available = available;
      state.variantId = variantId;

      if (buy.form) {
        buy.form._daileMvpSelection = {
          variantId: variantId,
          available: available,
          addonVariantId: withGlue && glueAvailable ? glueVariant : null
        };
      }
      buy.setPurchase({ variantId, available, hideExpress: withGlue, setMode: true, slug: r.value });

      const money = withGlue ? (r.getAttribute('data-money-glue') || r.getAttribute('data-money')) : r.getAttribute('data-money');
      state.priceText = money || '';
      buy.setPrice(money, withGlue ? '' : r.getAttribute('data-compare'));
      return r;
    };

    const render = () => {
      const r = sync();
      const tier = state.tier;
      const short = r.getAttribute('data-short') || '';

      incs.forEach((el) => { el.hidden = el.getAttribute('data-dv-pdp-inc') !== tier; });
      if (glueWrap) glueWrap.hidden = tier !== 'mini';

      pdp.querySelectorAll('[data-dv-pdp-price-short]').forEach((el) => { el.textContent = short; });
      pdp.querySelectorAll('[data-dv-pdp-price-pieces]').forEach((el) => { el.textContent = String(state.pieces); });
      pdp.querySelectorAll('[data-dv-pdp-price-glue]').forEach((el) => {
        const show = tier === 'mini' && !!glueVariant;
        el.hidden = !show;
        el.textContent = show ? '· ' + (state.glue ? glueWith : glueWithout) : '';
      });
      buy.all('[data-dv-pdp-sticky-label]').forEach((el) => {
        el.textContent = short + ' · ' + state.pieces + ' ' + piecesWord;
      });
      return r;
    };

    /* ?variant=<id of one of the 4> → that set; else ?set= alias; else the
       server's checked radio (Classic, or the ?variant= Liquid saw). A set
       without a variant ("Coming soon") is never a start state. */
    const startFromUrl = () => {
      let q;
      try { q = new URLSearchParams(window.location.search); } catch (e) { return null; }
      const variant = q.get('variant');
      if (variant) {
        const hit = radios.find((r) => r.getAttribute('data-variant-id') === String(variant));
        if (hit) return hit;
      }
      const alias = (q.get('set') || '').toLowerCase().replace(/[\s_-]/g, '');
      const map = { mini300: 'mini300', 300: 'mini300', mini500: 'mini500', 500: 'mini500', classic: 'classic', 1000: 'classic', signature: 'signature' };
      const key = map[alias];
      const hit = key ? radios.find((r) => r.value === key) : null;
      return hit && hit.getAttribute('data-exists') !== 'false' ? hit : null;
    };

    const start = startFromUrl();
    if (start) start.checked = true;
    const first = render();
    if (gallery) gallery.applySet(keysFor(first), false);

    radios.forEach((r) => on(r, 'change', () => {
      if (!r.checked) return;
      render();
      if (gallery) gallery.applySet(keysFor(r), true);
      replaceVariantParam(r.getAttribute('data-variant-id'), true);
      document.dispatchEvent(new CustomEvent('dv-pdp:set', { detail: Object.assign({}, state) }));
    }));
    if (glueInput) on(glueInput, 'change', () => {
      render();
      document.dispatchEvent(new CustomEvent('dv-pdp:set', { detail: Object.assign({}, state) }));
    });

    /* Safety net when daile-cart.js is absent: re-sync and block only an
       unavailable selection (daile-cart.js already stops the event on document). */
    if (buy.form) {
      on(buy.form, 'submit', (event) => {
        sync();
        if (!state.available) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);
    }

    return state;
  }

  /* ----------------------------------------------------------- generic variants */

  function initVariants(pdp, buy, gallery, on) {
    const box = pdp.querySelector('[data-dv-pdp-options]');
    const dataEl = pdp.querySelector('script[data-dv-pdp-variants]');
    if (!box || !dataEl) return null;
    let variants = [];
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return null; }
    variants.forEach((v) => { v.money = decode(v.money); v.compare = decode(v.compare); });
    const groups = Array.from(box.querySelectorAll('[data-dv-pdp-option]'));
    const state = { variantId: null, available: false, priceText: '' };

    const current = () => groups.map((g) => {
      const r = g.querySelector('input[type="radio"]:checked');
      return r ? r.value : null;
    });

    const markAvailability = (values) => {
      /* A value is available if some variant with it (and the other chosen
         values) can be bought: strike through the rest, keep them selectable. */
      groups.forEach((g, gi) => {
        g.querySelectorAll('input[type="radio"]').forEach((input) => {
          const ok = variants.some((v) => v.available && v.options[gi] === input.value &&
            v.options.every((o, oi) => oi === gi || values[oi] === null || values[oi] === o));
          const label = input.closest('.dv-choice');
          if (label) label.classList.toggle('is-unavailable', !ok);
        });
      });
    };

    const update = (fromUser) => {
      const values = current();
      const v = variants.find((x) => x.options.every((o, i) => values[i] === o));
      markAvailability(values);
      if (!v) {
        state.variantId = null;
        state.available = false;
        buy.setPurchase({ variantId: null, available: false, setMode: false });
        return;
      }
      state.variantId = v.id;
      state.available = !!v.available;
      state.priceText = v.money;
      buy.setPurchase({ variantId: v.id, available: !!v.available, setMode: false });
      buy.setPrice(v.money, v.compare);
      if (fromUser) {
        replaceVariantParam(v.id, false);
        if (gallery && v.media) gallery.focusMedia(v.media);
      }
    };

    groups.forEach((g) => g.querySelectorAll('input[type="radio"]').forEach((r) => on(r, 'change', () => update(true))));
    update(false);
    return state;
  }

  /* --------------------------------------------------------------- quantity */

  function initQuantity(pdp, on) {
    const input = pdp.querySelector('[data-dv-pdp-qty-input]');
    if (!input) return;
    pdp.querySelectorAll('[data-dv-pdp-qty]').forEach((btn) => {
      on(btn, 'click', () => {
        const step = parseInt(btn.getAttribute('data-dv-pdp-qty'), 10) || 0;
        const min = parseInt(input.min, 10) || 1;
        const now = parseInt(input.value, 10) || min;
        input.value = String(Math.max(min, now + step));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ------------------------------------------------------------ sticky pill */

  function initSticky(pdp, buy, on, cleanups) {
    const sticky = buy.sticky;
    if (!sticky || !buy.addBtn) return;
    /* Every .dv-section is its own stacking context (isolation), so a fixed
       pill inside the first chapter would slide under the next chapters.
       It lives in <body> while the section is on the page. */
    const home = sticky.parentNode;
    if (home && home !== document.body) {
      document.body.appendChild(sticky);
      cleanups.push(() => {
        if (home.isConnected) home.appendChild(sticky);
        else sticky.remove();
      });
    }
    let mainInView = true;
    let footerInView = false;
    /* As on the live PDP the pill shows whenever the main button is out of
       view, except while the page still rests at the very top: the first
       screen belongs to the artwork and its title (same 24px threshold as
       html.dv-page-scrolled). */
    let atTop = window.scrollY <= 24;
    const apply = () => {
      const show = !mainInView && !footerInView && !atTop;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (buy.stickyBtn) buy.stickyBtn.tabIndex = show ? 0 : -1;
    };
    let topRaf = 0;
    on(window, 'scroll', () => {
      if (topRaf) return;
      topRaf = requestAnimationFrame(() => {
        topRaf = 0;
        const now = window.scrollY <= 24;
        if (now !== atTop) { atTop = now; apply(); }
      });
    }, { passive: true });
    cleanups.push(() => { if (topRaf) cancelAnimationFrame(topRaf); });
    /* Photo mode: the pill is a link to the customizer, no submit. */
    if (buy.stickyBtn && buy.form) {
      on(buy.stickyBtn, 'click', () => {
        if (!buy.addBtn || buy.addBtn.disabled) return;
        if (typeof buy.form.requestSubmit === 'function') buy.form.requestSubmit(buy.addBtn);
        else buy.addBtn.click();
      });
    }
    if (!('IntersectionObserver' in window)) return;
    const ioMain = new IntersectionObserver((entries) => {
      entries.forEach((e) => { mainInView = e.isIntersecting; });
      apply();
    });
    ioMain.observe(buy.addBtn);
    const footer = document.querySelector('.shopify-section-group-footer-group, footer');
    let ioFoot = null;
    if (footer) {
      ioFoot = new IntersectionObserver((entries) => {
        entries.forEach((e) => { footerInView = e.isIntersecting; });
        apply();
      });
      ioFoot.observe(footer);
    }
    cleanups.push(() => { ioMain.disconnect(); if (ioFoot) ioFoot.disconnect(); });
  }

  /* --------------------------------------------------------------- reminder */

  function initRemind(pdp, getState, on) {
    const root = pdp.querySelector('[data-dv-pdp-remind]');
    if (!root) return;
    const company = root.getAttribute('data-kl-company');
    const listId = root.getAttribute('data-kl-list');
    const form = root.querySelector('[data-dv-pdp-remind-form]');
    const emailEl = root.querySelector('[data-dv-pdp-remind-email]');
    const newsEl = root.querySelector('[data-dv-pdp-remind-news]');
    const saveEl = root.querySelector('[data-dv-pdp-remind-save]');
    const msgEl = root.querySelector('[data-dv-pdp-remind-msg]');
    const chips = Array.from(root.querySelectorAll('[data-dv-pdp-remind-in]'));

    on(root, 'toggle', () => { if (root.open && emailEl) emailEl.focus({ preventScroll: true }); });

    const days = () => {
      const c = chips.find((x) => x.checked);
      return c ? parseInt(c.value, 10) || 7 : 7;
    };

    const say = (text, bad) => {
      if (!msgEl) return;
      msgEl.textContent = text;
      msgEl.classList.toggle('is-bad', !!bad);
      msgEl.hidden = false;
      if (emailEl) emailEl.setAttribute('aria-invalid', bad ? 'true' : 'false');
    };

    /* YYYY-MM-DD: Klaviyo reads it as a date, the flow starts on that day. */
    const dueDate = () => {
      const d = new Date(Date.now() + days() * 864e5);
      const pad = (n) => (n < 10 ? '0' : '') + n;
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
    };
    const niceDate = (iso) => {
      try {
        return new Date(iso + 'T12:00:00').toLocaleDateString(document.documentElement.lang || undefined, { day: 'numeric', month: 'long', numberingSystem: 'latn' });
      } catch (e) { return iso; }
    };

    const props = (due) => {
      const st = getState() || {};
      const title = pdp.querySelector('[data-dv-pdp-title]') || document.querySelector('h1');
      return {
        reminder_due: due,
        reminder_product: title ? title.textContent.trim() : document.title,
        reminder_url: location.origin + location.pathname,
        reminder_set: st.tier || '',
        reminder_pieces: st.pieces || '',
        reminder_price: st.priceText || '',
        daile_newsletter_opt_in: !!(newsEl && newsEl.checked)
      };
    };

    const klaviyo = (path, body) => fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify(body)
    });

    const submit = (e) => {
      if (e) e.preventDefault();
      const email = ((emailEl && emailEl.value) || '').trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email)) {
        say(root.getAttribute('data-msg-bad'), true);
        if (emailEl) emailEl.focus();
        return;
      }
      if (!company || !listId) { say(root.getAttribute('data-msg-err'), true); return; }
      const due = dueDate();
      const p = props(due);
      if (saveEl) saveEl.disabled = true;

      /* Subscription to the reminders list = consent to this one email. */
      klaviyo('subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'PDP reminder',
            profile: { data: { type: 'profile', attributes: { email: email, properties: p } } }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      }).then((res) => {
        if (!res.ok) throw new Error('klaviyo ' + res.status);
        /* Metric + second possible flow trigger (not critical). */
        klaviyo('events', {
          data: {
            type: 'event',
            attributes: {
              properties: p,
              metric: { data: { type: 'metric', attributes: { name: 'Requested Reminder' } } },
              profile: { data: { type: 'profile', attributes: { email: email } } }
            }
          }
        }).catch(() => {});
        root.classList.add('is-done');
        say(root.getAttribute('data-msg-ok') + ' ' + niceDate(due) + '.');
      }).catch(() => {
        if (saveEl) saveEl.disabled = false;
        say(root.getAttribute('data-msg-err'), true);
      });
    };
    if (form) on(form, 'submit', submit);
  }

  /* ----------------------------------------------------------- photo upload */

  const UPLOAD_MAX = 60 * 1024 * 1024; // worker MAX_ORIGINAL_MB (TASK98)
  const UPLOAD_TYPES = ['image/jpeg', 'image/png'];
  const IDB_TIMEOUT = 8000; // a blocked / hanging IndexedDB must not trap the buyer

  /* Same database, store, key and record formats as the customizer
     (assets/dv-customizer.js idbOpen / bootPdpPending / recordToBlob). */
  function idbSavePending(file) {
    return new Promise((resolve, reject) => {
      let rq;
      try { rq = window.indexedDB.open('daile-cz', 1); } catch (e) { reject(e); return; }
      rq.onupgradeneeded = () => {
        if (!rq.result.objectStoreNames.contains('photos')) rq.result.createObjectStore('photos');
      };
      rq.onerror = () => reject(rq.error);
      rq.onblocked = () => reject(new Error('indexedDB blocked'));
      rq.onsuccess = () => {
        const db = rq.result;
        const put = (val, onFail) => {
          let settled = false;
          const fail = (err) => { if (!settled) { settled = true; onFail(err); } };
          try {
            const tx = db.transaction('photos', 'readwrite');
            tx.objectStore('photos').put(val, 'photo_pdp-pending');
            tx.oncomplete = () => {
              if (settled) return;
              settled = true;
              try { db.close(); } catch (e) { /* ignore */ }
              resolve();
            };
            tx.onerror = () => fail(tx.error);
            tx.onabort = () => fail(tx.error);
          } catch (e) { fail(e); }
        };
        put(file, () => {
          /* A File that cannot be cloned (older Safari) → {buf, type}. */
          const fr = new FileReader();
          fr.onload = () => put({ buf: fr.result, type: file.type }, reject);
          fr.onerror = () => reject(fr.error);
          fr.readAsArrayBuffer(file);
        });
      };
    });
  }

  const withTimeout = (promise, ms) => new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('timeout')), ms);
    promise.then((v) => { window.clearTimeout(timer); resolve(v); }, (e) => { window.clearTimeout(timer); reject(e); });
  });

  function initUpload(pdp, buy, getSlug, on) {
    const box = pdp.querySelector('[data-dv-pdp-upload]');
    if (!box || !buy.photo) return;
    const input = box.querySelector('[data-dv-pdp-upload-input]');
    const errEl = box.querySelector('[data-dv-pdp-upload-error]');
    const label = box.querySelector('[data-dv-pdp-upload-label]');
    if (!input) return;
    const idle = label ? label.textContent : '';
    const msgType = box.getAttribute('data-msg-type') || 'Please upload a JPG or PNG photo.';
    const msgSize = box.getAttribute('data-msg-size') || 'This photo is over 60 MB — please use a smaller file (JPG works best).';
    const msgBusy = box.getAttribute('data-msg-busy') || '';
    let busy = false;

    const showError = (msg) => {
      if (!errEl) return;
      errEl.textContent = msg;
      errEl.hidden = false;
    };
    const setBusy = (state) => {
      busy = state;
      box.classList.toggle('is-busy', state);
      box.setAttribute('aria-busy', state ? 'true' : 'false');
      input.disabled = state;
      if (label && msgBusy) label.textContent = state ? msgBusy : idle;
    };

    const handle = (file) => {
      if (!file || busy) return;
      if (UPLOAD_TYPES.indexOf(file.type) === -1) { showError(msgType); input.value = ''; return; }
      if (file.size > UPLOAD_MAX) { showError(msgSize); input.value = ''; return; }
      if (errEl) errEl.hidden = true;
      setBusy(true);
      const slug = getSlug();
      const hasIdb = (() => { try { return !!window.indexedDB; } catch (e) { return false; } })();
      const saving = hasIdb ? withTimeout(idbSavePending(file), IDB_TIMEOUT) : Promise.reject(new Error('no indexedDB'));
      saving
        .then(() => { window.location.href = buy.czUrl(slug, true); })
        .catch(() => { window.location.href = buy.czUrl(slug, false); });
    };

    on(input, 'change', () => handle(input.files && input.files[0]));

    /* Drop a photo on the upload row (same checks as the picker). */
    const hasFiles = (e) => e.dataTransfer && Array.prototype.indexOf.call(e.dataTransfer.types || [], 'Files') !== -1;
    on(box, 'dragover', (e) => { if (hasFiles(e)) { e.preventDefault(); box.classList.add('is-drag'); } });
    on(box, 'dragleave', () => box.classList.remove('is-drag'));
    on(box, 'drop', (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      box.classList.remove('is-drag');
      handle(e.dataTransfer.files && e.dataTransfer.files[0]);
    });

    /* "Back" from the customizer restores this page from the bfcache with the
       busy state and the chosen file: reset both. */
    on(window, 'pageshow', () => {
      setBusy(false);
      try { input.value = ''; } catch (e) { /* ignore */ }
    });
  }

  /* ------------------------------------------------------------- main module */

  DV.register('dv-pdp', (root) => {
    const pdp = DV.queryAll(root, '[data-dv-pdp]')[0];
    if (!pdp) return undefined;
    const cleanups = [];
    const on = (el, type, fn, opts) => {
      el.addEventListener(type, fn, opts);
      cleanups.push(() => el.removeEventListener(type, fn, opts));
    };

    const galleryEl = pdp.querySelector('[data-dv-pdp-gallery]');
    const gallery = galleryEl ? initGallery(galleryEl, on) : null;
    const buy = buyState(pdp);
    const setState = initSets(pdp, buy, gallery, on);
    const variantState = setState ? null : initVariants(pdp, buy, gallery, on);
    initQuantity(pdp, on);
    initSticky(pdp, buy, on, cleanups);
    initUpload(pdp, buy, () => (setState && setState.available ? setState.slug : ''), on);
    initRemind(pdp, () => setState || variantState || { priceText: (pdp.querySelector('[data-dv-pdp-price-current]') || {}).textContent || '' }, on);

    /* Theme "recently viewed" list (kept from main-product-daile-mvp). */
    const pid = parseInt(pdp.getAttribute('data-product-id'), 10);
    if (pid) {
      try {
        let list = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
        list = list.filter((item) => item !== pid);
        list.unshift(pid);
        localStorage.setItem('theme:recently-viewed-products', JSON.stringify(list.slice(0, 20)));
      } catch (e) { /* private mode */ }
    }

    return {
      destroy() { cleanups.forEach((fn) => fn()); },
      onBlockSelect(block) {
        if (block && block.scrollIntoView) block.scrollIntoView({ block: 'nearest' });
      }
    };
  });

  /* FAQ: the NFC link needs the NFC dialog of dv-pdp-main. */
  DV.register('dv-pdp-faq', (root) => {
    const links = DV.queryAll(root, '[data-dv-pdp-nfc-trigger]');
    if (!links.length) return undefined;
    const has = !!document.getElementById('dv-pdp-nfc');
    links.forEach((a) => { a.hidden = !has; });
    return undefined;
  });

  /* Artist promo code: copy to clipboard. */
  DV.register('dv-pdp-artist', (root) => {
    const btn = DV.queryAll(root, '[data-dv-pdp-promo-copy]')[0];
    const code = DV.queryAll(root, '[data-dv-pdp-promo-code]')[0];
    if (!btn || !code) return undefined;
    const onClick = () => {
      const text = code.textContent.trim();
      const done = () => { if (typeof DV.toast === 'function') DV.toast(btn.getAttribute('data-copied') || 'Copied'); };
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(done).catch(() => {});
    };
    btn.addEventListener('click', onClick);
    return () => btn.removeEventListener('click', onClick);
  });
})();
